/**
 * Message List Component
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Message, ActiveTool } from '@bsp-chatbot/shared';
import { DocumentReference } from './DocumentReference';

interface MessageListProps {
  messages: Message[];
  isLoading?: boolean;
  activeTools?: ActiveTool[];
  reasoningText?: string;
}

export function MessageList({ messages, isLoading, activeTools = [], reasoningText = '' }: MessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const lastMessageCountRef = useRef(0);
  const [toolsExpanded, setToolsExpanded] = useState(false);

  // Check if user is near the bottom of the scroll container
  const checkIfNearBottom = useCallback(() => {
    const container = containerRef.current?.parentElement;
    if (!container) return true;
    const threshold = 100;
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  }, []);

  // Handle scroll events to track user position
  useEffect(() => {
    const container = containerRef.current?.parentElement;
    if (!container) return;

    const handleScroll = () => {
      isNearBottomRef.current = checkIfNearBottom();
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [checkIfNearBottom]);

  // Auto-scroll logic
  useEffect(() => {
    const container = containerRef.current?.parentElement;
    if (!container) return;

    const isNewMessage = messages.length > lastMessageCountRef.current;
    lastMessageCountRef.current = messages.length;

    // Always scroll on new message, otherwise only if near bottom
    if (isNewMessage || isNearBottomRef.current) {
      // Use requestAnimationFrame for smooth, non-blocking scroll
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
    }
  }, [messages]);

  // Find the streaming message (last assistant message while loading)
  const streamingMessageId = isLoading && messages.length > 0 && messages[messages.length - 1]?.role === 'assistant'
    ? messages[messages.length - 1].id
    : null;

  return (
    <div ref={containerRef} className="p-6 space-y-6">
      {messages.map((message) => (
        <div key={message.id}>
          {/* Show ProcessingStatus right before the streaming assistant message */}
          {isLoading && (activeTools.length > 0 || reasoningText) && message.id === streamingMessageId && (
            <div className="mb-6">
              <ProcessingStatus
                activeTools={activeTools}
                expanded={toolsExpanded}
                onToggleExpand={() => setToolsExpanded(!toolsExpanded)}
                reasoningText={reasoningText}
              />
            </div>
          )}
          <MessageItem
            message={message}
            isStreaming={message.id === streamingMessageId}
          />
        </div>
      ))}
    </div>
  );
}

interface MessageItemProps {
  message: Message;
  isStreaming?: boolean;
}

function MessageItem({ message, isStreaming }: MessageItemProps) {
  const isUser = message.role === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] ${
          isUser
            ? 'bg-primary-600 text-white rounded-2xl rounded-tr-md'
            : 'bg-white border border-gray-200 rounded-2xl rounded-tl-md shadow-sm'
        } px-5 py-4`}
      >
        {/* Thinking block (for extended thinking) */}
        {message.thinking && (
          <details className="mb-3">
            <summary className="cursor-pointer text-sm text-amber-600 hover:text-amber-700">
              View reasoning
            </summary>
            <div className="thinking-block mt-2 text-xs whitespace-pre-wrap">
              {message.thinking}
            </div>
          </details>
        )}

        {/* Message content */}
        {isUser ? (
          <p className="whitespace-pre-wrap">{message.content}</p>
        ) : message.content ? (
          <div className="markdown-content prose prose-sm max-w-none">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                table: ({ children }) => (
                  <div className="overflow-x-auto my-4">
                    <table className="min-w-full border-collapse border border-gray-300 text-sm">
                      {children}
                    </table>
                  </div>
                ),
                thead: ({ children }) => (
                  <thead className="bg-gray-100">{children}</thead>
                ),
                th: ({ children }) => (
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold">
                    {children}
                  </th>
                ),
                td: ({ children }) => (
                  <td className="border border-gray-300 px-3 py-2">{children}</td>
                ),
                code: ({ className, children, ...props }) => {
                  const isInline = !className;
                  return isInline ? (
                    <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono text-primary-700" {...props}>
                      {children}
                    </code>
                  ) : (
                    <code className={`${className} block bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm`} {...props}>
                      {children}
                    </code>
                  );
                },
                pre: ({ children }) => (
                  <pre className="my-4">{children}</pre>
                ),
              }}
            >
              {message.content}
            </ReactMarkdown>
            {/* Streaming cursor */}
            {isStreaming && (
              <span className="inline-block w-2 h-4 bg-gray-400 animate-pulse ml-0.5 align-text-bottom" />
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-gray-500 min-h-[24px]">
            <div className="w-4 h-4 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm italic">Thinking...</span>
          </div>
        )}

        {/* Document references */}
        {message.documentReferences && message.documentReferences.length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-500 mb-2">
              Referenced documents:
            </p>
            <div className="flex flex-wrap gap-2">
              {message.documentReferences.map((ref, index) => (
                <DocumentReference key={index} reference={ref} />
              ))}
            </div>
          </div>
        )}

        {/* Timestamp */}
        <p
          className={`text-xs mt-2 ${
            isUser ? 'text-primary-200' : 'text-gray-400'
          }`}
        >
          {new Date(message.timestamp).toLocaleTimeString()}
        </p>
      </div>
    </div>
  );
}

/**
 * Get human-readable display name for a tool
 */
function getToolDisplayName(toolName: string): string {
  const displayNames: Record<string, string> = {
    list_documents: 'Document Index',
    get_document: 'Document Content',
    search_content: 'Search',
    get_template_structure: 'Template Structure',
    get_template_sheet: 'Template Sheet',
  };
  return displayNames[toolName] || toolName.replace(/_/g, ' ');
}

/**
 * Processing Status Component - shows tool execution progress and reasoning text
 */
interface ProcessingStatusProps {
  activeTools: ActiveTool[];
  expanded: boolean;
  onToggleExpand: () => void;
  reasoningText?: string;
}

function ProcessingStatus({ activeTools, expanded, onToggleExpand, reasoningText = '' }: ProcessingStatusProps) {
  const completedTools = activeTools.filter((t) => t.status !== 'running');
  const runningTool = activeTools.find((t) => t.status === 'running');
  const completedCount = completedTools.length;

  // Current status text
  const currentStatus = runningTool
    ? `Querying ${getToolDisplayName(runningTool.toolName)}...`
    : 'Processing...';

  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
        {/* Reasoning text */}
        {reasoningText && (
          <p className="text-sm text-gray-700 mb-3 italic">{reasoningText}</p>
        )}

        {/* Main status line with spinner */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            <span className="italic">{currentStatus}</span>
          </div>

          {/* Expandable section showing completed queries */}
          {completedCount > 0 && (
            <button
              onClick={onToggleExpand}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
            >
              <span>
                {completedCount} {completedCount === 1 ? 'query' : 'queries'}
              </span>
              <svg
                className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          )}
        </div>

        {/* Expanded details with success/error icons */}
        {expanded && completedTools.length > 0 && (
          <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
            {completedTools.map((tool) => (
              <div key={tool.toolId} className="flex items-center gap-2 text-xs text-gray-600">
                {tool.status === 'success' ? (
                  <svg className="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
                <span>
                  {getToolDisplayName(tool.toolName)} {tool.status === 'success' ? 'complete' : 'failed'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
